import React from 'react';

import { EmptyState, ErrorState, Skeleton } from '../../ui';
import { formatarDataHora } from '../../utils/data';

/**
 * Histórico de edições de um registro administrado pelo TI (at-69, frente D/D7).
 *
 * 🔴 **Nasce porque os TRÊS editores desenhavam o mesmo bloco, cada um do seu
 * jeito** — template de termo, plano de cotação e motocicleta elétrica. Os três
 * mostram a mesma coisa (quando foi editado + o snapshot anterior), com três CSS
 * próprios e três textos de vazio diferentes. Convenção da at-24: regra repetida
 * em componente é divergência com data marcada.
 *
 * ⚠️ **Mora em `components/`, não no kit.** Ele conhece o vocabulário do domínio
 * ("edição", "snapshot anterior") e os três consumidores são telas de TI — a
 * regra do `13-ui-kit.md` é que `src/ui` não conhece domínio. O que ele usa do
 * kit (`Skeleton`, `ErrorState`, `EmptyState`) é puro. Mesmo critério do
 * `ResumoRegistro`, da at-67.
 *
 * ⚠️ **Componente burro**: não busca nada. Quem carrega é a ficha, quando a aba
 * abre — carregar junto com o registro custaria uma requisição que a maioria das
 * visitas (que vêm corrigir um campo) não usa.
 *
 * 📌 **`editadoPorId` não é exibido**, nas três fichas, e isso não é esquecimento:
 * o backend guarda o **id** do usuário, não o nome. Mostrar um UUID seria pior que
 * não mostrar nada; exibir o nome exigiria uma rota que não existe.
 */

export interface ItemHistorico {
  id: string;
  /** ISO vindo do backend. Formatado aqui, com o formato único do app (at-66). */
  quando: string;
  /** Linha principal: o que o registro era antes desta edição. */
  resumo: React.ReactNode;
  /** Bloco opcional abaixo (o corpo anterior do termo, por exemplo). */
  detalhe?: React.ReactNode;
}

interface PropsHistoricoEdicoes {
  itens: ItemHistorico[];
  carregando: boolean;
  /** Mensagem de falha. Presente = a leitura não aconteceu. */
  erro?: string;
  aoTentarNovamente?: () => void;
  /** Texto do vazio — cada registro descreve o próprio "nunca foi editado". */
  vazio: string;
}

const HistoricoEdicoes: React.FC<PropsHistoricoEdicoes> = ({
  itens,
  carregando,
  erro,
  aoTentarNovamente,
  vazio,
}) => {
  if (carregando) return <Skeleton linhas={3} altura="tw-h-16" />;

  /* 🔴 Falha de leitura NÃO pode virar "nenhuma edição registrada": as duas
     frases levam a conclusões opostas sobre o registro (lição da at-66). */
  if (erro) {
    return (
      <ErrorState
        mensagem="Não foi possível carregar o histórico."
        detalhe={erro}
        aoTentarNovamente={aoTentarNovamente}
      />
    );
  }

  if (itens.length === 0) {
    return <EmptyState icone="relogio" titulo={vazio} />;
  }

  return (
    <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
      {itens.map((item) => (
        <li
          key={item.id}
          className="tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white tw-p-4"
        >
          <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-texto-suave">
            {formatarDataHora(item.quando)}
          </p>

          <div className="tw-mt-1 tw-text-sm tw-text-texto-corpo [overflow-wrap:anywhere]">
            {item.resumo}
          </div>

          {item.detalhe && <div className="tw-mt-2">{item.detalhe}</div>}
        </li>
      ))}
    </ol>
  );
};

export default HistoricoEdicoes;
